<template>
  <v-app id="app" :class="{ 'is-mobile': isMobile }">
    <!-- Connection Error Screen -->
    <AppConnectionError
      v-if="connectionError && !user"
      :error="connectionError"
      :on-retry="retryConnection"
    />

    <!-- Normal App Content -->
    <template v-else>
      <AppSetupContextWorkaround />
      <V2ShellRestaurantDrawer
        v-if="restaurant && !isFullPage"
        :restaurant="restaurant"
        :user="user"
        :role="role"
        :branches="branches"
        :branch="branch"
      />
      <V2ShellUserDrawer
        v-else-if="!!user && showMainMenu"
        :user="user"
      />
      <V2ShellTopBar
        v-if="user && showAppBar"
        :restaurant="restaurant"
        :printing-enabled="!!printingEnabled"
        @logout="logout"
      />
      <!-- Sizes your content based upon application components -->
      <v-content>
        <!-- Provides the application the proper gutter -->
        <v-container
          fluid
          class="pa-0 flex-column content-shell"
          :class="{ 'pos-shell': isViewportShell, 'pos-shell--full': isFullPage }"
        >
          <AppConnectivityBanner v-if="!isFullPage" />
          <V2ShellNotice
            v-if="restaurant && planNotice && !noticeHidden && !isFullPage"
            :tone="planNotice.tone"
            :title="
              $tc(planNotice.titleKey, planNotice.diffDays, {
                n: planNotice.diffDays,
              })
            "
            :subtitle="$t(planNotice.subtitleKey)"
            :help="$t(planNotice.helpKey)"
            :action-text="$t(planNotice.actionKey)"
            dismissible
            @action="renew"
            @dismiss="hideNotice"
          />
          <V2ShellVerifyNotice
            v-else-if="user && showVerifyBar && !isFullPage"
            :user="user"
          />
          <V2ShellContextBar
            :show-branch="!!showBranchSelector"
            :show-caja="!!showCajaSelector"
          />
          <v-row justify="space-between" class="align-self-stretch">
            <v-col cols="12" class="pt-0 pb-0">
              <!-- If using vue-router -->
              <router-view></router-view>
            </v-col>
          </v-row>
          <!-- Restaurant pages surface the language selector inside the
               sidebar; POS pages are fixed-height workspaces with no footer. -->
          <v-row
            justify="space-between"
            class="align-self-stretch"
            v-if="user && !restaurant && !isPos"
          >
            <v-col
              cols="12"
              class="pt-0 text-center"
              :class="{ 'pb-8': !!user }"
            >
              <v-container fluid>
                <language-selector></language-selector>
              </v-container>
            </v-col>
          </v-row>
        </v-container>
      </v-content>
      <!-- The «Asistente» panel inside stores; the GPTHelp bubble on every
           page without it, guests included (plan §9.14). -->
      <V2AssistantRoot />
      <V2ShellAlerts :alerts="alerts" />
      <modals-container />
      <V2ShellUpdatePrompt v-if="updateAvailable" @update="update" />
    </template>
  </v-app>
</template>

<script lang="ts">
  import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
  import { IAlert } from './interfaces/IAlert';
  import AppConnectionError from './components/AppConnectionError.vue';
  import { IUser } from './interfaces/IUser';
  import { IRestaurant } from './interfaces/IRestaurant';
  import { IClubProgramPublic } from './interfaces/IClubProgram';
  import LanguageSelector from './components/LanguageSelector.vue';
  import { Globals } from './modules/Globals';
  import AuthService from './api/services/auth.service';
  import { vueData } from './VueData';
  import { IBranch } from './interfaces/IBranch';
  import { Socket } from 'vue-socket.io-extended';
  import { IMember } from './interfaces/IMember';
  import OrdersService from './api/services/orders.service';
  import { TicketPrintSetting } from './enums/PrintEnum';
  import { PrinterType, PrintPluginStatus } from './modules/PrintService';
  import { IPrintSettings } from './interfaces/IPrintSettings';
  import { IOrder } from './interfaces/IOrder';
  import { OrderMode } from './enums/OrderEnums';
  import { PrintFormat } from './lib/print/EscPosEncoder';
  import { EventBus } from './modules/EventBus';
  import { bootstrap, setOptions } from 'vue-gtag';
  import UsersService from './api/services/users.service';
  import { IRestaurantSettings } from './api/services/restaurants.service';
  import { CapacitorSplashScreen } from '@/lib/capacitor/CapacitorSplashScreen';
  import { CapacitorApp } from '@/lib/capacitor/CapacitorApp';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { useRestaurant } from './composition/use-restaurant';
  import { usePrinting } from './composition/use-printing';
  import { initPrintingBackup } from './composition/printing/use-printing-backup';
  import { clearWebViewCacheSafe } from '@/lib/capacitor/WebViewCache';
  import { applyLegacyShellFullscreen } from '@/lib/capacitor/LegacyShellCompat';
  import { initCustomerDisplay } from '@/composition/use-customer-display';
  import { useIdlePrefetch } from '@/composition/use-idle-prefetch';
  import { useOrders } from './composition/use-orders';
  import { useMenu } from './composition/use-menu';
  import AppSetupContextWorkaround from './components/AppSetupContextWorkaround.vue';
  import AppConnectivityBanner from './components/AppConnectivityBanner.vue';
  import { getEffectiveTier } from '@/utils/billing-entitlements.utils';
  import { computePlanNotice } from '@/utils/plan-notice.utils';
  import type { IPlanNotice, TPlanNoticeKind } from '@/utils/plan-notice.utils';
  import { isNewerVersion } from '@/utils/version.utils';
  import V2ShellAlerts from '@/components/v2/shell/V2ShellAlerts.vue';
  import V2ShellContextBar from '@/components/v2/shell/V2ShellContextBar.vue';
  import V2ShellNotice from '@/components/v2/shell/V2ShellNotice.vue';
  import V2ShellRestaurantDrawer from '@/components/v2/shell/V2ShellRestaurantDrawer.vue';
  import V2ShellTopBar from '@/components/v2/shell/V2ShellTopBar.vue';
  import V2ShellUpdatePrompt from '@/components/v2/shell/V2ShellUpdatePrompt.vue';
  import V2ShellUserDrawer from '@/components/v2/shell/V2ShellUserDrawer.vue';
  import V2ShellVerifyNotice from '@/components/v2/shell/V2ShellVerifyNotice.vue';
  import V2AssistantRoot from '@/components/v2/assistant/V2AssistantRoot.vue';
  import { deepActiveElement } from '@/lib/dom/deep-active-element';

  @Component({
    components: {
      LanguageSelector,
      AppSetupContextWorkaround,
      AppConnectionError,
      AppConnectivityBanner,
      V2ShellAlerts,
      V2ShellContextBar,
      V2ShellNotice,
      V2ShellRestaurantDrawer,
      V2ShellTopBar,
      V2ShellUpdatePrompt,
      V2ShellUserDrawer,
      V2ShellVerifyNotice,
      V2AssistantRoot,
    },
  })
  export default class App extends Vue {
    @Prop() alerts: IAlert[];
    @Prop() user: IUser;
    @Prop() role: IMember;
    @Prop() restaurant: IRestaurant;
    @Prop() restaurantSettings: IRestaurantSettings;
    @Prop() branches: IBranch[];
    @Prop() branch: IBranch;
    @Prop() version: string;
    @Prop() latestVersion: string;
    @Prop() country: string;
    @Prop() connectionError: Error | null;
    restaurantStore = useRestaurantStore();
    usersService = new UsersService();
    ordersService = new OrdersService();
    authService = new AuthService();
    whatsappMessage = encodeURIComponent(
      'Hola! Tengo una consulta sobre el menú digital'
    );
    isMobile = Globals.isMobile;
    isNative = Globals.isCapacitor;
    apiPath = Globals.apiPath;
    noticeHidden = false;
    realtimeConnected = true;
    lastOrderReceiveTime = null;
    orderNotifyInterval = null;
    restaurantHelper = useRestaurant(true);
    printing = usePrinting(true);
    menuHelper = useMenu(true);

    created() {
      this.realtimeConnected =
        this.$socket && this.$socket.client && this.$socket.client.connected;
      this.initAnalytics();
    }

    mounted() {
      // @ts-ignore
      window.PTT = this.printing;

      this.onBranchChanged(this.branch, undefined);

      //if (!this.isMobile) this.initFbChat();

      EventBus.$on('ordersRead', () => {
        this.lastOrderReceiveTime = null;
      });

      if (Globals.isCapacitor) {
        CapacitorApp.initBackButton();
        CapacitorSplashScreen.hide();
        // Android-only inside: printing-config durability via Preferences
        // (restore-if-empty once identity resolves, then debounced backups).
        initPrintingBackup();
        // Android-only inside (bails on other platforms).
        initCustomerDisplay({
          getRouteName: () => this.$route?.name ?? null,
          getIsDark: () => this.$vuetify.theme.dark,
        });
        // Old APKs rely on the web bundle to hide the system bars.
        if (Globals.isLegacyAndroidShell) applyLegacyShellFullscreen();
      }

      useIdlePrefetch();
    }

    async initAnalytics() {
      const loc = await this.usersService.getLocation();
      const country = loc[2];

      const options: any = {
        config: { id: 'UA-171078765-2' },
      };

      setOptions(options);

      bootstrap().then(() => {
        /*setTimeout(() => {
          this.$gtag.pageview({
            page_path: this.$route.path,
          })
        }, 1000)*/
      });
    }

    beforeDestroy() {
      EventBus.$off('ordersRead');
      if (this.orderNotifyInterval) clearInterval(this.orderNotifyInterval);
      if (this.branch && this.$socket && this.$socket.client) {
        this.$socket.client.emit('lR', { r: this.branch.restaurantId });
      }
      if (this.posScrollWatchdogActive) {
        window.removeEventListener('scroll', this.onPosWindowScroll);
        this.posScrollWatchdogActive = false;
      }
    }

    initFbChat() {
      window['fbAsyncInit'] = function () {
        if (window['FB']) {
          window['FB'].init({
            xfbml: true,
            version: 'v7.0',
          });
        }
      };

      (function (d, s, id) {
        var js,
          fjs = d.getElementsByTagName(s)[0];
        if (d.getElementById(id)) return;
        js = d.createElement(s);
        js.id = id;
        js.src = 'https://connect.facebook.net/es_LA/sdk/xfbml.customerchat.js';
        fjs.parentNode.insertBefore(js, fjs);
      })(document, 'script', 'facebook-jssdk');
    }

    private updateCurrency() {
      if (!vueData.restaurant) vueData.setCurrency(null);
      else {
        const id = vueData.restaurant.currency;
        const currency = Globals.currencies.find((value) => value.value === id);
        vueData.setCurrency(currency);
      }
    }

    get effectiveTier(): number {
      return getEffectiveTier(this.restaurant);
    }

    get printingEnabled() {
      return (
        (!this.isMobile || Globals.isCapacitor) &&
        this.restaurant &&
        this.effectiveTier > 0 &&
        //&& this.restaurant.beta
        this.branch &&
        this.branch.allowOrders &&
        this.branch.ordersMode === OrderMode.Native
      );
    }

    renew() {
      if (!this.restaurant) return;

      const noticeKind = this.planNoticeCandidate?.kind ?? 'legacy_expiring';

      this.$gtag.event('renew', {
        event_category: 'plan_notice',
        event_label: this.restaurant.id,
        noticeKind,
      });
      this.$router.push(`/${this.restaurant.stringId}/settings?tab=plan`);
    }

    hideNotice() {
      if (!this.restaurant) return;

      const noticeKind: TPlanNoticeKind =
        this.planNoticeCandidate?.kind ?? 'legacy_expiring';

      this.$gtag.event('hide', {
        event_category: 'plan_notice',
        event_label: this.restaurant.id,
        noticeKind,
      });
      this.noticeHidden = true;
      if (Globals.hasLocalStorage()) {
        const payload = JSON.stringify({ time: Date.now() });
        localStorage.setItem(
          this.planNoticeHideKey(this.restaurant.id, noticeKind),
          payload
        );
        if (noticeKind === 'legacy_expiring') {
          // Backward compatible key (used by older clients).
          localStorage.setItem(`hideNoticeData${this.restaurant.id}`, payload);
        }
      }
    }

    private planNoticeHideKey(
      restaurantId: number,
      kind: TPlanNoticeKind
    ): string {
      return `hidePlanNotice:${restaurantId}:${kind}`;
    }

    private readPlanNoticeHiddenUntilMs(
      restaurantId: number,
      kind: TPlanNoticeKind
    ): number | null {
      if (!Globals.hasLocalStorage()) return null;

      const dayMs = 1000 * 60 * 60 * 24;
      const readUntil = (raw: string | null): number | null => {
        if (!raw) return null;
        try {
          const parsed = JSON.parse(raw);
          const time = parsed?.time;
          if (typeof time !== 'number' || !Number.isFinite(time)) return null;
          return time + dayMs;
        } catch (e) {
          return null;
        }
      };

      const key = this.planNoticeHideKey(restaurantId, kind);
      const hiddenUntil = readUntil(localStorage.getItem(key));
      if (hiddenUntil != null) return hiddenUntil;

      // Backward compatibility for the legacy notice.
      if (kind === 'legacy_expiring') {
        return readUntil(localStorage.getItem(`hideNoticeData${restaurantId}`));
      }

      return null;
    }

    get isPos() {
      return !!this.$route.meta?.pos;
    }

    // Viewport-exact pages: POS workspaces and canvas pages (route meta
    // `viewport`, e.g. the Menú page) own the height and scroll inside; the
    // notice bars above them become rows that shrink the page.
    get isViewportShell() {
      return this.isPos || !!this.$route.meta?.viewport;
    }

    // Chromeless flows (store setup): no app bar above, no notice bars inside,
    // no connectivity banner (the flows show their own offline banner).
    get isFullPage() {
      return !!this.$route.meta?.fullPage;
    }

    get updateAvailable(): boolean {
      return isNewerVersion(this.latestVersion, this.version);
    }

    // The POS shell is a fixed-height workspace: document scroll is never
    // valid there, but mobile browsers can still offset the layout viewport
    // themselves (on-screen keyboard, smooth scrollIntoView racing layout),
    // leaving the page stuck behind the app bar. This watchdog snaps it back.
    posScrollWatchdogActive = false;
    posScrollRafPending = false;

    onPosWindowScroll() {
      if (this.posScrollRafPending) return;
      this.posScrollRafPending = true;
      window.requestAnimationFrame(() => {
        this.posScrollRafPending = false;
        this.resetPosDocumentScroll();
      });
    }

    resetPosDocumentScroll() {
      if (!this.isViewportShell || window.scrollY <= 0) return;

      // Don't fight the browser while it reveals a focused field above the
      // on-screen keyboard; the next scroll event after blur restores 0.
      // The field may sit in a shadow root (the assistant's composer).
      const active = deepActiveElement();
      if (
        active instanceof HTMLElement &&
        (active.tagName === 'INPUT' ||
          active.tagName === 'TEXTAREA' ||
          active.isContentEditable)
      ) {
        return;
      }

      window.scrollTo(0, 0);
    }

    @Watch('isViewportShell', { immediate: true })
    onIsViewportShellChanged(isViewportShell: boolean) {
      if (isViewportShell && !this.posScrollWatchdogActive) {
        window.addEventListener('scroll', this.onPosWindowScroll, {
          passive: true,
        });
        this.posScrollWatchdogActive = true;
        this.resetPosDocumentScroll();
      } else if (!isViewportShell && this.posScrollWatchdogActive) {
        window.removeEventListener('scroll', this.onPosWindowScroll);
        this.posScrollWatchdogActive = false;
      }
    }

    get showVerifyBar() {
      if (!this.restaurant || !this.user || this.user.emailVerified)
        return false;

      const dayMs = 1000 * 60 * 60 * 24;
      const now = Date.now();
      const createdAt = new Date(this.user.createdAt);

      return now - createdAt.getTime() >= dayMs;
    }

    async update() {
      if (Globals.isCapacitor) {
        await clearWebViewCacheSafe();
      }
      window.location.reload();
    }

    get planNoticeCandidate(): IPlanNotice | null {
      return computePlanNotice({
        restaurant: this.restaurant ?? null,
        role: this.role ?? null,
        effectiveTier: this.effectiveTier,
        restaurantSettings: this.restaurantSettings ?? null,
        subscription: this.restaurantStore.billingSubscriptionSummary ?? null,
        subscriptionStatus:
          this.restaurantStore.billingSubscriptionStatus ?? 'idle',
      });
    }

    get planNotice(): IPlanNotice | null {
      const notice = this.planNoticeCandidate;
      if (!notice) return null;
      if (this.noticeHidden) return null;

      const restaurantId = this.restaurant?.id ?? null;
      if (!restaurantId) return notice;
      if (!Globals.hasLocalStorage()) return notice;

      const hiddenUntilMs = this.readPlanNoticeHiddenUntilMs(
        restaurantId,
        notice.kind
      );
      if (!hiddenUntilMs) return notice;

      return Date.now() < hiddenUntilMs ? null : notice;
    }

    @Watch('restaurant.currency')
    onCurrencyChanged(newId, oldId) {
      this.updateCurrency();
    }

    @Watch('restaurant')
    onRestaurantChanged(
      newVal: IRestaurant | null,
      oldVal: IRestaurant | null
    ) {
      this.noticeHidden = false;

      // The catalog (and the legacy POS cache) follow the restaurant through
      // useMenu(true)'s coalesced sync.
      if (oldVal) {
        this.$socket.client.removeListener(
          `r:${oldVal.id}:clubU`,
          this.onClubProgramUpdate
        );
      }

      if (newVal) {
        this.$socket.client.addEventListener(
          `r:${newVal.id}:clubU`,
          this.onClubProgramUpdate
        );
      }
    }

    /**
     * Club program settings changed (clubU). Payload is the public program
     * mapping or null (program paused/inactive); mirror it into the store so
     * club-gated surfaces (ModalPay, club attach, topup) stay current.
     */
    onClubProgramUpdate(program: IClubProgramPublic | null) {
      if (!this.restaurant) return;
      if (Globals.IsLocalhost)
        console.log('Server club program updated, applying to store');
      this.$set(this.restaurant, 'club', program);
    }

    @Watch('branch')
    onBranchChanged(newVal: IBranch, oldVal: IBranch) {
      setTimeout(() => {
        if (oldVal) {
          this.$socket.client.emit('lR', { r: oldVal.restaurantId });
        }

        if (newVal) {
          this.$socket.client.emit('jR', { r: newVal.restaurantId });
        }
      }, 10); // Wait until branches are set too
    }

    get showBranchSelector() {
      return (
        this.$route.meta.branchSelector &&
        this.effectiveTier > 1 &&
        this.branches &&
        this.branches.length > 1
      );
    }

    get showCajaSelector() {
      return (
        this.$route.meta.cajaSelector &&
        this.effectiveTier > 0 &&
        this.branch.settingUseCajas &&
        !!this.restaurantStore.cajas
      );
    }

    @Socket()
    connect() {
      //console.log('Realtime connected');
      this.realtimeConnected = true;
    }

    @Socket()
    disconnect() {
      //console.log('Realtime disconnected');
      this.realtimeConnected = false;
    }

    @Socket()
    reconnect() {
      //console.log('Realtime reconnected');
      this.realtimeConnected = true;
      if (this.restaurant)
        this.$socket.client.emit('jR', { r: this.restaurant.id });
    }

    @Watch('realtimeConnected', { immediate: true })
    onRealtimeConnectedChange(newVal: boolean, oldVal: boolean) {
      if (newVal && !oldVal && this.restaurant) {
        this.restaurantHelper.reloadCajas().catch(console.error);
        this.restaurantStore.loadPrintStations().catch(console.error);
      }
    }

    get showMainMenu() {
      return this.$route.name !== 'verify' && !this.$route.meta.noMenus;
    }

    get showAppBar() {
      return !this.$route.meta.noMenus;
    }

    async logout() {
      await this.authService.logout();
      vueData.setUser(null);
      this.$router.push('/');
    }

    async retryConnection() {
      try {
        // Reload the page to retry initialization
        window.location.reload();
      } catch (error) {
        console.error('Error retrying connection:', error);
      }
    }
  }
</script>

<style lang="scss">
  @import 'assets/styles/styles.css';
  @import 'assets/styles/styles.scss';
  @import 'assets/payments/style.css';

  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
  }

  // The canvas token (src/theme/tokens.js); the id beats Vuetify's theme class.
  #app.v-application {
    background: rgb(var(--c-canvas));
  }

  .v-navigation-drawer--clipped:not(.v-navigation-drawer--temporary):not(.v-navigation-drawer--is-mobile) {
    z-index: 6 !important;
  }

  // Contain all page-content z-indexes (sticky table columns, chips, in-page
  // popovers) in one stacking context so fixed chrome mounted outside the
  // container (app bar z:5, drawer z:6, root-attached v-menu/v-dialog) always
  // paints above content — without this, any content z-index > 5 covers the
  // nav bar when it scrolls underneath. Unlike transform/filter, isolation
  // does not create a containing block, so position: fixed descendants keep
  // viewport positioning. Same technique as .pos-shell below.
  .content-shell {
    isolation: isolate;
  }

  // POS routes and canvas pages (route meta `viewport`) render as fixed-height
  // workspaces: the shell owns the viewport
  // height (app bar excluded) and pages fill it, scrolling internally. Notice
  // bars (plan/verify) become flex children that shrink the workspace instead
  // of overflowing the document (which glitched the nav bar on scroll).
  .pos-shell {
    --nav-height: 48px;
    height: calc(100vh - var(--nav-height));
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    overflow: hidden;
    overscroll-behavior: none;
    // Contain all POS-internal z-indexes in one stacking context so
    // app-level chrome mounted at the root (drawer, v-menu popovers,
    // dialogs) always paints above the workspace. Unlike transform/filter,
    // isolation does not create a containing block, so position: fixed
    // descendants (mobile nav, print panels) keep viewport positioning.
    isolation: isolate;

    // dvh must live in @supports: declarations containing var() skip parse-time
    // validation, so a plain dvh line would override and then invalidate the vh
    // fallback on browsers without dvh (Chrome < 108), collapsing the shell to 0.
    @supports (height: 100dvh) {
      height: calc(100dvh - var(--nav-height));
    }

    // Vuetify toolbars default to flex: 1 1 auto: a toolbar or header child
    // would grow to fill the column and eat the workspace.
    > .v-toolbar,
    > header {
      flex: 0 0 auto;
    }

    > .row {
      flex: 1 1 0;
      min-height: 0;
      margin: 0;

      > .col-12 {
        height: 100%;
        min-width: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
      }
    }

    // Old POS page keeps its own root class; make it fill the shell so it
    // stays viewport-exact even when a notice bar is visible.
    > .row > .col-12 > .pos-page {
      height: 100%;
    }
  }

  // Full-page flows render without the app bar: the shell owns the whole viewport.
  .pos-shell.pos-shell--full {
    --nav-height: 0px;
  }
</style>
